<template>
	<div>
		<section
			ref="heroBannerVideo"
			:class="heroClass"
			class="hero-banner-video"
			v-on="!video.autoplay ? { click: triggerPlayVideo } : {}"
		>
			<LinkWrap
				v-bind="linkWrap ? linkWrap : null"
				style="max-width: 100%"
				custom-type="div"
			>
				<ImageResponsive
					v-if="image && showImage"
					auto-aspect
					class="responsive-image-fullscreen"
					v-bind="image"
				/>

				<figure>
					<div
						v-if="!showImage"
						ref="videoContainer"
						:style="{ aspectRatio: calculatedAspectRatio }"
						class="video-container"
					>
						<div
							:style="{ aspectRatio: calculatedAspectRatio }"
							class="video-wrapper"
						>
							<TnVideo
								ref="videoPlayer"
								:autoplay="video.autoplay"
								:controls="false"
								:loop="video.loop"
								:muted="video.muted"
								:src-desktop="video.landscapeVideoLink"
								:src-mobile="video.portraitVideoLink"
								:src-youtube="video.url"
								@metadata-ready="setBannerAspectRatio"
							/>
						</div>
					</div>
				</figure>

				<div
					v-if="showContent"
					:class="[
						content.textColor,
						{ 'hidden-mobile': isMobile && hideContentOnMobile, 'black-text': invertTextColor },
					]"
					class="content-parent"
				>
					<TnHeading
						v-if="content.heroTitle"
						type="h1"
						>{{ content.heroTitle }}
					</TnHeading>
					<RichText
						v-if="content.heroRichText"
						:text="content.heroRichText"
						class="padding-vertical-m richtext"
					/>
					<TnButton
						v-if="button"
						class="margin-vertical-xl"
						v-bind="button"
					></TnButton>
				</div>
			</LinkWrap>
		</section>
	</div>
</template>

<script>
import ResponsiveImageViewModel from "~/helpers/ViewModels/ResponsiveImageViewModel.js";
import ButtonViewModel from "~/helpers/ViewModels/ButtonViewModel.js";
import AnchorViewModel from "~/helpers/ViewModels/AnchorViewModel.js";
import LinkViewModel from "~/helpers/ViewModels/LinkViewModel.js";
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";

const heroAspectWidth = 16;
const heroAspectHeight = 9;

export default defineNuxtComponent({
	name: "HeroBannerVideo",

	mixins: [GizmoComponentMixin],

	data() {
		return {
			heroAspectRatio: "",
			heroAspectWidth,
			heroAspectHeight,
		};
	},

	computed: {
		invertTextColor() {
			return this.isMobile && this.content.textColor === "white" && !this.content.dark;
		},
		isTwe() {
			return (
				(this.component.template && this.component.template.name.indexOf("twe") > -1) || this.component.content.isTwe
			);
		},
		video() {
			return this.component && this.content.heroVideo;
		},
		button() {
			return ButtonViewModel(this.content.button);
		},
		linkWrap() {
			return LinkViewModel(this.content?.linkWrap);
		},
		image() {
			return ResponsiveImageViewModel(this.content.heroResponsiveImage);
		},
		showContent() {
			return !!(this.content.heroTitle || this.content.heroRichText || this.button);
		},
		hideContentOnMobile() {
			const hideMobile = this.component && this.content.hideContentOnMobile;
			return this.isMobile && hideMobile;
		},
		showImage() {
			return (
				(this.isMobile && !this.video.portraitVideoLink) ||
				(!this.isMobile && !(this.video.landscapeVideoLink || this.video.url))
			);
		},
		isMobile() {
			return this.$store.getters.isMobile;
		},
		heroClass() {
			if (this.isTwe) return "t-we";
			if (this.showContent) return this.content.textColor || "black";
			return "";
		},
		anchorLink() {
			if (this.content.heroInternalAnchor) return AnchorViewModel(this.content.heroInternalAnchor);
			return "";
		},
		calculatedAspectRatio() {
			return this.heroAspectRatio?.replace(":", "/");
		},
	},

	methods: {
		triggerPlayVideo() {
			const videoHtml = this.$refs.videoPlayer.$refs.videoHtml;
			if (videoHtml) {
				videoHtml.togglePlayPause();
				return;
			}
			const videoYoutube = this.$refs.videoPlayer.$refs.videoYoutube;
			if (videoYoutube) {
				videoYoutube.togglePlayPause();
			}
		},
		setBannerAspectRatio() {
			const videoHtml = this.$refs.videoPlayer.$refs.html;
			if (videoHtml) {
				this.heroAspectRatio = videoHtml.getAspectRatio();
				[this.heroAspectWidth, this.heroAspectHeight] = this.heroAspectRatio.split(":");
			}
		},
	},
});
</script>

<style lang="scss" scoped>
.richtext {
	@include font-text-l;
}

.hero-banner-video {
	position: relative;

	.video-container {
		position: relative;
		margin-bottom: 0;

		@include breakpoint(ultra) {
			max-height: 90vh;
			width: 100vw;
		}
	}

	.video-wrapper {
		min-width: 100%;
		position: absolute;
		top: 0;
		bottom: 0;
		left: 50%;
		transform: translateX(-50%);
		margin: 0;

		@include breakpoint(mobile) {
			transform: translateX(0%);
			left: 0;
			width: 100%;

			:deep(video) {
				width: 100%;
				object-fit: fill;
				position: absolute;
			}
		}
	}

	.responsive-image :deep(img) {
		object-fit: fill;
		width: 100%;
		object-position: 50% 0;
	}

	.responsive-image-fullscreen :deep(img) {
		@include breakpoint(mobile) {
			width: 100%;
		}
	}

	.tn-heading {
		@include breakpoint(mobile) {
			@include font-title-l;
		}
	}

	.content-parent {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		position: absolute;
		bottom: $spacing-3xl;
		padding: 0 $spacing-m;
		width: 100%;
		max-width: 800px;
		left: 50%;
		transform: translateX(-50%);

		@include breakpoint(mobile) {
			padding-bottom: 40px;
			padding-top: 40px;
			left: auto;
			transform: none;
			position: absolute;
			bottom: 0;
			width: 100%;
		}
	}

	figure {
		position: relative;
		width: 100vw;
		max-height: none;
		overflow: hidden;
		display: block;
		max-width: 100%;

		&::after {
			content: "";
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			z-index: 0;
			background: transparent;
		}
	}

	&.black {
		:deep(p),
		h1,
		h2,
		h3 {
			color: $color-text;
			text-align: center;
		}

		.content-parent {
			@include breakpoint(mobile) {
				background: rgb(255 255 255 / 80%);
			}
		}

		@media (width >= 768px) {
			figure::after {
				background: $color-neutrals-white;
				opacity: 0.3;
			}
		}
	}

	&.white {
		:deep(p),
		h1,
		h2,
		h3 {
			color: $color-neutrals-white;
			text-align: center;
		}

		.content-parent {
			@include breakpoint(mobile) {
				background: rgb(0 0 0 / 80%);
			}
		}

		@media (width >= 768px) {
			figure::after {
				background: $color-twe-background;
				opacity: 0.3;
			}
		}
	}

	&.t-we {
		figure::after {
			background: linear-gradient(-180deg, transparent, $color-twe-background 100%);
		}

		.content-parent:not(.black-text) {
			:deep(p),
			h1,
			h2,
			h3 {
				color: $color-neutrals-white;
				text-align: center;
			}
		}

		:deep(a) {
			border-bottom: none;
			color: $color-twe-cta-default;

			&:hover {
				text-decoration: underline;
			}
		}

		.content-parent {
			@include breakpoint(mobile) {
				position: relative;
				margin: 0 auto;
			}
		}
	}
}
</style>
